import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import Home from "@/pages/Home";
import ProductDetail from "@/pages/ProductDetail";
import StorePublic from "@/pages/StorePublic";
import Cuponera from "@/pages/Cuponera";
import NeniStore from "@/pages/NeniStore";
import Cart from "@/pages/Cart";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import MultiPago from "@/pages/MultiPago";
import PlazasDirectory from "@/pages/PlazasDirectory";
import RegistrarPlaza from "@/pages/RegistrarPlaza";
import ParaLocatarios from "@/pages/ParaLocatarios";
import NenisPlaceLanding from "@/pages/NenisPlaceLanding";
import DriversLanding from "@/pages/DriversLanding";
import RepartidorOnboarding from "@/pages/RepartidorOnboarding";
import CustomerDashboard from "@/pages/dashboard/CustomerDashboard";
import StoreDashboard from "@/pages/dashboard/StoreDashboard";
import PlazaAdminDashboard from "@/pages/dashboard/PlazaAdminDashboard";
import DriverDashboard from "@/pages/dashboard/DriverDashboard";
import NeniDashboard from "@/pages/dashboard/NeniDashboard";

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-zinc-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-zinc-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const map = {
    plaza_admin: "/admin",
    tienda: "/tienda",
    repartidor: "/repartidor",
    cliente: "/cliente",
    neni: "/neni",
  };
  return <Navigate to={map[user.role] || "/"} replace />;
}

export default function App() {
  // Auto-recarga en fallos de carga de chunk (post-deploy con hashes nuevos)
  useEffect(() => {
    const onRej = (e) => {
      // PromiseRejectionEvent expose 'reason'; ErrorEvent expose 'error' + 'message'
      const msg = String(
        e?.reason?.message || e?.reason ||
        e?.error?.message || e?.message || ""
      );
      const name = String(e?.reason?.name || e?.error?.name || "");
      if (
        name === "ChunkLoadError" ||
        /Loading chunk .* failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(msg)
      ) {
        if (!sessionStorage.getItem("__chunk_reload__")) {
          sessionStorage.setItem("__chunk_reload__", "1");
          window.location.reload();
        }
      }
    };
    window.addEventListener("unhandledrejection", onRej);
    window.addEventListener("error", onRej);
    // Clear flag on successful mount (evita loops persistentes)
    const t = setTimeout(() => sessionStorage.removeItem("__chunk_reload__"), 3000);
    return () => {
      window.removeEventListener("unhandledrejection", onRej);
      window.removeEventListener("error", onRej);
      clearTimeout(t);
    };
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/producto/:id" element={<ProductDetail />} />
              <Route path="/t/:tiendaId" element={<StorePublic />} />
              <Route path="/cuponera/:codigo" element={<Cuponera />} />
              <Route path="/carrito" element={<Cart />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/pago/:ordenId" element={<Protected roles={["cliente"]}><MultiPago /></Protected>} />
              <Route path="/plazas" element={<PlazasDirectory />} />
              <Route path="/registrar-plaza" element={<RegistrarPlaza />} />
              <Route path="/para-locatarios" element={<ParaLocatarios />} />
              <Route path="/para-repartidores" element={<DriversLanding />} />
              <Route path="/nenis-place" element={<NenisPlaceLanding />} />
              <Route path="/dashboard" element={<RoleRedirect />} />
              <Route path="/cliente" element={<Protected roles={["cliente"]}><CustomerDashboard /></Protected>} />
              <Route path="/tienda" element={<Protected roles={["tienda"]}><StoreDashboard /></Protected>} />
              <Route path="/admin" element={<Protected roles={["plaza_admin"]}><PlazaAdminDashboard /></Protected>} />
              <Route path="/repartidor" element={<Protected roles={["repartidor"]}><DriverDashboard /></Protected>} />
              <Route path="/repartidor/onboarding" element={<Protected roles={["repartidor"]}><RepartidorOnboarding /></Protected>} />
              <Route path="/neni" element={<Protected roles={["neni"]}><NeniDashboard /></Protected>} />
              <Route path="/:slug" element={<NeniStore />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </CartProvider>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
